<template>
  <!-- Create account, in the shared signed-out frame (world map left, form right). -->
  <AuthShell wide hide-lang>
    <template v-if="registrationDisabled">
      <h2 class="auth-title">{{ t('auth.registrationClosed') }}</h2>
      <p class="auth-sub">{{ registrationMessage }}</p>
      <div class="auth-links"><router-link to="/login">{{ t('auth.goToSignIn') }}</router-link></div>
    </template>
    <Form v-else @submit="onSubmit" :validation-schema="schema" v-slot="{ errors }">
      <h2 class="auth-title">{{ t('auth.registerTitle') }}</h2>
      <p class="auth-sub">{{ t('auth.registerSub') }}</p>

      <!-- language first, so the rest of the form can be read in it (both names shown, whichever is active) -->
      <label class="auth-field">
        <span class="auth-field__label">Language / 语言</span>
        <select :value="locale" @change="setLocale($event.target.value)">
          <option v-for="l in LANGUAGES" :key="l.code" :value="l.code">{{ l.name }}</option>
        </select>
      </label>

      <div class="auth-grid">
        <label class="auth-field">
          <span class="auth-field__label">{{ t('auth.fullName') }}</span>
          <Field name="name" v-slot="{ field }">
            <input v-bind="field" type="text" name="name" autocomplete="name" placeholder="Jane Doe" />
          </Field>
          <span v-if="errors.name" class="auth-error">{{ errors.name }}</span>
        </label>
        <label class="auth-field">
          <span class="auth-field__label">{{ t('auth.username') }}</span>
          <Field name="user_name" v-slot="{ field }">
            <input v-bind="field" type="text" name="user_name" autocomplete="username" placeholder="janedoe" />
          </Field>
          <span v-if="errors.user_name" class="auth-error">{{ errors.user_name }}</span>
        </label>
      </div>
      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.email') }}</span>
        <Field name="email" v-slot="{ field }">
        <input v-bind="field" type="email" name="email" autocomplete="email" placeholder="name@company.com" />
        </Field>
        <span v-if="errors.email" class="auth-error">{{ errors.email }}</span>
      </label>
      <div class="auth-grid">
        <label class="auth-field">
          <span class="auth-field__label">{{ t('auth.password') }}</span>
          <Field name="password" v-slot="{ field }">
            <input v-bind="field" type="password" name="password" autocomplete="new-password" />
          </Field>
          <span v-if="errors.password" class="auth-error">{{ errors.password }}</span>
        </label>
        <label class="auth-field">
          <span class="auth-field__label">{{ t('auth.confirmPassword') }}</span>
          <Field name="confirm_password" v-slot="{ field }">
            <input v-bind="field" type="password" name="confirm_password" autocomplete="new-password" />
          </Field>
          <span v-if="errors.confirm_password" class="auth-error">{{ errors.confirm_password }}</span>
        </label>
      </div>
      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.address') }}</span>
        <Field name="address" v-slot="{ field }">
        <input v-bind="field" type="text" name="address" autocomplete="street-address" :placeholder="t('auth.addressPlaceholder')" />
        </Field>
        <span v-if="errors.address" class="auth-error">{{ errors.address }}</span>
      </label>
      <div class="auth-grid">
        <label class="auth-field">
          <span class="auth-field__label">{{ t('auth.country') }}</span>
          <Field name="country" v-slot="{ field }">
            <country-select v-bind="field" name="country" v-model="selectedCountry" :country="selectedCountry" topCountry="AU" :countryName="true" />
          </Field>
          <span v-if="errors.country" class="auth-error">{{ errors.country }}</span>
        </label>
        <label class="auth-field">
          <span class="auth-field__label">{{ t('auth.region') }}</span>
          <Field name="city" v-slot="{ field }">
            <region-select v-bind="field" name="city" v-model="selectedRegion" :country="selectedCountry" :region="selectedRegion" :countryName="true" :regionName="true" />
          </Field>
          <span v-if="errors.city" class="auth-error">{{ errors.city }}</span>
        </label>
      </div>
      <label class="auth-field">
        <span class="auth-field__label">{{ t('auth.telegram') }}</span>
        <Field name="tglink" v-slot="{ field }">
        <input v-bind="field" type="text" name="tglink" placeholder="https://t.me/username" />
        </Field>
        <span v-if="errors.tglink" class="auth-error">{{ errors.tglink }}</span>
      </label>

      <template v-if="requiresRecaptcha">
      <Field name="recaptcha" type="hidden" :model-value="reCaptchaV2Token" />
      <div class="auth-captcha" :class="{ 'is-error': Boolean(errors.recaptcha) }">
        <vue-recaptcha :sitekey="accountStore.reCaptchaSiteKey" size="normal" :hl="locale === 'zh-CN' ? 'zh-CN' : 'en'" :key="locale" theme="dark"
          :loading-timeout="recaptchaTimeout" @verify="recaptchaVerified"
          @expire="recaptchaExpired" @error="recaptchaError">
        </vue-recaptcha>
      </div>
      <span v-if="errors.recaptcha" class="auth-error" style="margin: -10px 0 12px;">{{ errors.recaptcha }}</span>
      </template>

      <button class="auth-btn" type="submit" :disabled="Boolean(loading)">{{ loading ? t('auth.creatingAccount') : t('auth.createAccount') }}</button>
      <div class="auth-links"><router-link to="/login">{{ t('auth.haveAccount') }}</router-link></div>
    </Form>
  </AuthShell>
</template>

<script setup>
import { t, locale, setLocale, LANGUAGES } from '@/i18n';
import { Form, Field } from 'vee-validate';
import AuthShell from '../AuthShell.vue';
import * as Yup from 'yup';
import vueRecaptcha from 'vue3-recaptcha2';
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useAccountStore, useAlertStore } from '@/stores';
import { useRoute } from 'vue-router';
import { computed } from 'vue';

const route = useRoute();
const alertStore = useAlertStore();
const accountStore = useAccountStore();
const { loading, registrationDisabled, registrationMessage } = storeToRefs(accountStore)
const selectedCountry = ref("")
const selectedRegion = ref("")
const currentUrl = computed(() => route.fullPath);
const queryParamR = computed(() => route.query.r);

const requiresRecaptcha = !!process.env.VUE_APP_VUE_RECAPTCHA_V2_SITE_KEY   // reCAPTCHA once this site has its own key (.env)
const schema = Yup.object().shape({
  email: Yup.string().required(() => t('auth.v.emailRequired')).email(() => t('auth.v.emailInvalid')),
  password: Yup.string().required(() => t('auth.v.passwordRequired')).min(8, () => t('auth.v.passwordMin')),
  confirm_password: Yup.string().required(() => t('auth.v.confirmRequired')).oneOf([Yup.ref('password')], () => t('auth.v.passwordsMatch')),
  user_name: Yup.string().required(() => t('auth.v.userNameRequired')),
  name: Yup.string().required(() => t('auth.v.fullNameRequired')),
  address: Yup.string().required(() => t('auth.v.addressRequired')),
  city: Yup.string().required(() => t('auth.v.regionRequired')),
  country: Yup.string().required(() => t('auth.v.countryRequired')),
  ...(requiresRecaptcha ? { recaptcha: Yup.string().required(() => t('auth.v.captcha')) } : {}),
});

const recaptchaTimeout = ref(30000);
const reCaptchaV2Token = ref('');

function recaptchaVerified(response) {
  reCaptchaV2Token.value = response;
}

function recaptchaExpired() {
  reCaptchaV2Token.value = '';
}

function recaptchaError(reason) {
  reCaptchaV2Token.value = '';
  alertStore.error(t('auth.recaptchaError'));
}

async function onSubmit(values) {
  try {
    // const url = new URL(currentUrl);
    // const referral = url.searchParams.get('r');
    await accountStore.signup(values, route.query.r);
  } catch (error) {
    console.error(error)
  }
}

</script>
